/**
 * The pixel shape of an image file, measured in the only place that can.
 *
 * ## Why this is a module and not four lines in the dialog
 *
 * The same reason `posterSearch` is: **the server has no decoder**, so if the
 * browser does not measure a file, nobody ever does — and a measurement that
 * only exists inside a component cannot be tested without one either. jsdom
 * loads no resources, so an `<img>` there fires neither `load` NOR `error`.
 * Extracted, the caller can be tested against a stub and this can be tested
 * against its own fake loader.
 *
 * ## 🚨 The timeout is not defensive padding
 *
 * The first version awaited `onload`/`onerror` and nothing else. In jsdom that
 * never settles — which is how this was found — but the same shape is reachable
 * in a real browser: a decoder that stalls on a malformed file leaves the
 * promise pending forever, and the caller sits on "Uploading…" with no error,
 * no upload and no way back except reloading the page.
 *
 * An unmeasurable file is a normal outcome, not a failure. `0 x 0` is a legal
 * row in the catalogue and most of the production table is exactly that, so
 * every unhappy path here resolves NULL and the upload proceeds. Refusing would
 * be inventing a rule the catalogue does not have.
 */

/** Generous for a decode, short enough that nobody watches a dead dialog. */
export const MEASURE_TIMEOUT_MS = 3_000;

export interface PixelSize {
  width: number;
  height: number;
}

/**
 * How an image gets decoded. Injected so the timeout and the null-paths can be
 * tested without a real decoder — the loader is the only impure part.
 */
export type ImageLoader = (file: File) => Promise<PixelSize | null>;

/** The real one: an `<img>` pointed at an object URL. */
export const domImageLoader: ImageLoader = (file) =>
  new Promise((resolve) => {
    let url: string;
    try {
      url = URL.createObjectURL(file);
    } catch {
      // No object URLs here (an older embedded webview, a locked-down
      // environment). Unmeasured, not failed.
      resolve(null);
      return;
    }

    const img = new Image();
    const done = (size: PixelSize | null) => {
      URL.revokeObjectURL(url);
      resolve(size);
    };
    img.onload = () =>
      done(
        img.naturalWidth && img.naturalHeight
          ? { width: img.naturalWidth, height: img.naturalHeight }
          : null
      );
    img.onerror = () => done(null);
    img.src = url;
  });

/**
 * Measure `file`, or answer null — never hang, never throw.
 *
 * ⚠️ The object URL is revoked by the loader on BOTH of its paths. A leaked
 * blob URL pins the whole file in memory for the life of the document, and a
 * 10 MB poster per attempt adds up in a dialog somebody is iterating in.
 * Note the timeout does NOT revoke: the loader still owns that url and may
 * still settle, and revoking underneath it would be a second bug.
 */
export async function measureImage(
  file: File,
  load: ImageLoader = domImageLoader,
  timeoutMs: number = MEASURE_TIMEOUT_MS
): Promise<PixelSize | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      load(file).catch(() => null),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}
